import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { agentLogQuery, type AgentLogRow } from '../api/devices'
import { LENGTHS, pageButtons } from '../ui/DataTable'
import { Spinner } from '../ui/Spinner'

/**
 * Fila Agent Log - opteemum/devices/details/agentLog.php: When / Level / Step /
 * Message, fraza de gol, chevronul Debug. odUtcWhen e aici, fara moment.js.
 */

const TABLE = 'agentErrorLog'
const EMPTY =
  'Nothing reported by the agent. That is the healthy state. A line appears here when a step (sync, monitor, self-update) fails, or when debug logs have been retrieved.'
/** `odOpenDeviceSheet('remoteEventViewer/') || odOpenDeviceSheet('deviceRemoteEvents/')` din agentLog.php. */
const EVENT_VIEWER_TABS = ['opteemum/remoteEventViewer/', 'proactivitee/deviceRemoteEvents/']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function utcWhen(raw: string): { text: string; title: string } {
  if (!raw) return { text: '', title: '' }
  const utc = new Date(raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z')
  if (Number.isNaN(utc.getTime())) return { text: raw, title: '' }
  const pad = (n: number) => String(n).padStart(2, '0')
  const title = `${utc.getUTCFullYear()}-${pad(utc.getUTCMonth() + 1)}-${pad(utc.getUTCDate())} ${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())}:${pad(utc.getUTCSeconds())} UTC`
  const text = `${pad(utc.getDate())} ${MONTHS[utc.getMonth()]} ${utc.getFullYear()} ${pad(utc.getHours())}:${pad(utc.getMinutes())}:${pad(utc.getSeconds())}`
  return { text, title }
}

export function levelTone(level: string): string {
  const lv = level.toLowerCase()
  if (lv === 'debug' || lv === 'trace') return '#6f7b8a'
  if (lv === 'warn' || lv === 'warning') return '#9e6b00'
  if (lv === 'info') return '#1f62cb'
  return '#b42318'
}

export function isDebugLevel(level: string): boolean {
  const lv = level.toLowerCase()
  return lv === 'debug' || lv === 'trace'
}

export function DeviceAgentLog({ id, open, hasTab }: { id: number; open?: (href: string) => void; hasTab?: (href: string) => boolean }) {
  const [start, setStart] = useState(0)
  const [length, setLength] = useState(10)
  const [order, setOrder] = useState<{ column: number; dir: 'asc' | 'desc' }>({ column: 0, dir: 'desc' })
  const [debugOpen, setDebugOpen] = useState(false)
  const table = useQuery({
    ...agentLogQuery(id, { start, length, orderColumn: order.column, orderDir: order.dir, search: '' }),
    placeholderData: keepPreviousData,
  })

  if (table.error && !table.data) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {table.error.message}
      </div>
    )
  }
  if (!table.data) return <Spinner />

  const rows = table.data.data
  const debugCount = rows.filter((r) => isDebugLevel(r.level)).length

  return (
    <div className="row">
      <div className="col-md-12">
        <p className="text-muted" style={{ margin: '0 0 12px', fontSize: 12 }}>
          What the OptimumDesk agent itself reported — the same lines as <code>error.log</code> on the
          machine (UTC, shown in your timezone). The operating system journal is{' '}
          <a
            href="javascript:void(0)"
            className="js-open-event-viewer"
            onClick={(e) => {
              e.preventDefault()
              /* Ca in agentLog.php: intai fila Opteemum, apoi cea Proactivitee.
                 Masina o are pe una sau pe alta, dupa modulele si drepturile ei,
                 deci se deschide aia pe care fisa chiar o are. */
              const tab = EVENT_VIEWER_TABS.find((href) => hasTab?.(href))
              if (tab) open?.(tab)
            }}
          >
            Remote Event Viewer
          </a>
          .
        </p>
        <div className="agent-log-tools" style={{ display: debugCount ? undefined : 'none', margin: '0 0 10px' }}>
          <button type="button" className="btn btn-default btn-xs js-toggle-debug" onClick={() => setDebugOpen((v) => !v)}>
            <i className={`fas ${debugOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true"></i>{' '}
            <span className="js-toggle-debug-label">{debugOpen ? 'Hide debug lines' : 'Show debug lines'}</span>{' '}
            <span className="js-toggle-debug-count label" style={{ background: '#6f7b8a', fontWeight: 'normal' }}>
              {debugCount}
            </span>
          </button>
        </div>
        <LogTable
          rows={rows}
          total={table.data.recordsFiltered}
          fetching={table.isFetching}
          start={start}
          length={length}
          order={order}
          debugOpen={debugOpen}
          onStart={setStart}
          onLength={(n) => {
            setLength(n)
            setStart(0)
          }}
          onOrder={(next) => {
            setOrder(next)
            setStart(0)
          }}
        />
      </div>
    </div>
  )
}

function LogTable({
  rows,
  total,
  fetching,
  start,
  length,
  order,
  debugOpen,
  onStart,
  onLength,
  onOrder,
}: {
  rows: AgentLogRow[]
  total: number
  fetching: boolean
  start: number
  length: number
  order: { column: number; dir: 'asc' | 'desc' }
  debugOpen: boolean
  onStart(n: number): void
  onLength(n: number): void
  onOrder(next: { column: number; dir: 'asc' | 'desc' }): void
}) {
  const pages = Math.max(1, Math.ceil(total / length) || 1)
  const page = Math.floor(start / length)
  const from = total === 0 ? 0 : start + 1
  const to = Math.min(start + length, total)
  const heads: { title: string; width?: number; orderable: boolean }[] = [
    { title: 'When', width: 180, orderable: true },
    { title: 'Level', width: 80, orderable: true },
    { title: 'Step', width: 160, orderable: true },
    { title: 'Message', orderable: true },
  ]

  return (
    <div className="dataTables_wrapper form-inline dt-bootstrap no-footer" id={`${TABLE}_wrapper`}>
      <div className="row">
        <div className="col-sm-12 multiActions"></div>
      </div>
      <div className="row">
        <div className="col-sm-6 dataTableF"></div>
        <div className="col-sm-6 dataTableA"></div>
      </div>
      <div className="row">
        <div className="col-sm-12 userFilters"></div>
      </div>
      <div className="row">
        <div className="col-sm-12">
          <div className="dataTables_processing" id={`${TABLE}_processing`} style={{ display: fetching ? 'block' : 'none' }}>
            Processing...
          </div>
          <table className="table table-striped table-hover dataTable agent-error-log-table no-footer" width="100%" id={TABLE} role="grid">
            <thead>
              <tr role="row">
                {heads.map((col, i) => {
                  const sorted = order.column === i
                  const cls = [
                    'td-small a-left',
                    col.orderable ? (sorted ? (order.dir === 'asc' ? 'sorting_asc' : 'sorting_desc') : 'sorting') : 'sorting_disabled',
                  ].join(' ')
                  return (
                    <th
                      key={col.title}
                      className={cls}
                      style={col.width ? { width: col.width } : undefined}
                      aria-label={`${col.title}: activate to sort column ${sorted && order.dir === 'asc' ? 'descending' : 'ascending'}`}
                      onClick={() => onOrder({ column: i, dir: sorted && order.dir === 'desc' ? 'asc' : 'desc' })}
                    >
                      {col.title}
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="odd">
                  <td colSpan={4} className="dataTables_empty">
                    {EMPTY}
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => (
                  <tr
                    key={row.DT_RowId}
                    id={row.DT_RowId}
                    role="row"
                    className={i % 2 === 0 ? 'odd' : 'even'}
                    style={isDebugLevel(row.level) && !debugOpen ? { display: 'none' } : undefined}
                  >
                    <WhenCell at={row.occurred_at} />
                    <LevelCell level={row.level} />
                    <StepCell step={row.step} />
                    <td className="td-small a-left">
                      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.message}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="row">
        <div className="col-sm-2">
          <div className="dataTables_length custom_length_class" id={`${TABLE}_length`}>
            <label>
              Show{' '}
              <select
                name={`${TABLE}_length`}
                aria-controls={TABLE}
                className="form-control input-sm"
                value={length}
                onChange={(e) => onLength(Number(e.target.value))}
              >
                {LENGTHS.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>{' '}
              entries
            </label>
          </div>
        </div>
        <div className="col-sm-4 aright">
          <div className="dataTables_info" id={`${TABLE}_info`} role="status" aria-live="polite">
            Showing {from} to {to} of {total} entries
          </div>
        </div>
        <div className="col-sm-6">
          <div className="dataTables_paginate paging_simple_numbers" id={`${TABLE}_paginate`}>
            <ul className="pagination">
              <li className={`paginate_button previous${page <= 0 ? ' disabled' : ''}`} id={`${TABLE}_previous`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page > 0) onStart((page - 1) * length)
                  }}
                >
                  Previous
                </a>
              </li>
              {pageButtons(page, pages).map((b, i) =>
                b === 'ellipsis' ? (
                  <li key={`e${i}`} className="paginate_button disabled">
                    <a href="#" onClick={(e) => e.preventDefault()}>
                      …
                    </a>
                  </li>
                ) : (
                  <li key={b} className={`paginate_button${b === page ? ' active' : ''}`}>
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault()
                        onStart(b * length)
                      }}
                    >
                      {b + 1}
                    </a>
                  </li>
                ),
              )}
              <li className={`paginate_button next${page >= pages - 1 ? ' disabled' : ''}`} id={`${TABLE}_next`}>
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault()
                    if (page < pages - 1) onStart((page + 1) * length)
                  }}
                >
                  Next
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="clear"></div>
    </div>
  )
}

function WhenCell({ at }: { at: string }) {
  const when = utcWhen(at)
  return (
    <td className="td-small a-left">
      {when.text ? <span title={when.title}>{when.text}</span> : null}
    </td>
  )
}

function LevelCell({ level }: { level: string }) {
  return (
    <td className="td-small a-left">
      {level ? (
        <span className="label" style={{ background: levelTone(level), fontWeight: 'normal' }}>
          {level.toLowerCase()}
        </span>
      ) : (
        <span className="text-muted">&mdash;</span>
      )}
    </td>
  )
}

function StepCell({ step }: { step: string }) {
  return (
    <td className="td-small a-left">
      {step ? (
        <span className="label" style={{ background: '#6f7b8a', fontWeight: 'normal' }}>
          {step}
        </span>
      ) : null}
    </td>
  )
}
